// @ts-check
/** @typedef {import('../types/api').AdminUser} AdminUser */
/** @typedef {import('../types/api').AdminUserRole} AdminUserRole */
/** @typedef {import('../types/api').AdminUserPermissions} AdminUserPermissions */

import { Check, Shield, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import adminUserService from '../services/adminUserService';

const UserRolesPage = () => {
  /** @type {[AdminUserRole[], Function]} */
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [showPermissionsModal, setShowPermissionsModal] = useState(null);

  const fetchUsers = useCallback((page, limit) => adminUserService.getAllUsers({ page, limit }), []);
  const {
    items: users,
    loading: usersLoading,
    reset: resetUsers,
    sentinelRef,
  } = useInfiniteScroll(fetchUsers, { limit: 50, deps: [] });

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [rolesData, currentUserData] = await Promise.all([
          adminUserService.getAvailableRoles(),
          adminUserService.getCurrentUserPermissions(),
        ]);
        setRoles(rolesData);
        setCurrentUser(currentUserData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadMeta();
  }, []);

  // Dirty whenever the dropdown differs from the user's actual saved role —
  // a derived comparison, not accumulated state, since this is the only
  // editable field in this modal.
  const isRoleChangeDirty = !!selectedUser && selectedRole !== selectedUser.role;

  const handleChangeRoleCloseClick = () => {
    if (isRoleChangeDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setSelectedUser(null);
  };

  const handleRoleChange = async (user) => {
    if (!selectedRole) {
      alert('Please select a role');
      return;
    }

    try {
      await adminUserService.updateUserRole(user.id, selectedRole);
      setSelectedUser(null);
      setSelectedRole('');
      resetUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update role: ' + (error.detail || error.message));
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
      manager: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      staff: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      content_editor: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
      viewer: 'bg-muted text-foreground',
    };
    return colors[role] || 'bg-muted text-foreground';
  };

  const getRoleIcon = (role) => {
    if (role === 'super_admin') return '👑';
    if (role === 'manager') return '📊';
    if (role === 'staff') return '👤';
    if (role === 'content_editor') return '✏️';
    if (role === 'viewer') return '👁️';
    return '👤';
  };

  if (initialLoading) {
    return <LoadingScreen />;
  }

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-serif">User Roles & Permissions</h1>
          <p className="text-muted-foreground mt-1">Manage admin users and their access levels</p>
        </div>
      </div>

      {/* Current User Info */}
      {currentUser && (
        <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4 flex items-start space-x-3">
          <Shield className="text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-300">Your Role: {currentUser.role_name}</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              You have {currentUser.permissions.length} permissions
              {!isSuperAdmin && ' (Only Super Admins can change user roles)'}
            </p>
          </div>
        </div>
      )}

      {/* Roles Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {roles.map((role) => (
          <div key={role.key} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">{getRoleIcon(role.key)}</span>
              <h3 className="font-medium text-sm">{role.name}</h3>
            </div>
            <p className="text-xs text-muted-foreground">{role.permission_count} permissions</p>
            <button
              onClick={() => setShowPermissionsModal(role)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2"
            >
              View Permissions
            </button>
          </div>
        ))}
      </div>

      {/* Users Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium">Admin Users</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Permissions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                {isSuperAdmin && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-foreground">{user.full_name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}
                    >
                      <span>{getRoleIcon(user.role)}</span>
                      <span>{user.role.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{user.permissions.length} permissions</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${
                        user.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                      }`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  {isSuperAdmin && (
                    <td className="px-6 py-4 text-right">
                      {user.id !== currentUser?.user_id && (
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setSelectedRole(user.role);
                          }}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
                        >
                          Change Role
                        </button>
                      )}
                      {user.id === currentUser?.user_id && (
                        <span className="text-muted-foreground/70 text-sm">You</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="py-4 flex justify-center">
        {usersLoading && users.length > 0 && <LoadingSpinner size="sm" />}
      </div>

      {/* Change Role Modal */}
      {selectedUser && (
        <Modal
          onClose={handleChangeRoleCloseClick}
          maxWidth="md"
          fullHeight={false}
          className="rounded-lg shadow-xl"
          closeOnBackdrop={false}
          hasUnsavedChanges={isRoleChangeDirty}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="text-lg font-medium">Change User Role</h3>
            <button onClick={handleChangeRoleCloseClick} className="text-muted-foreground/70 hover:text-foreground">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground/80">User</p>
              <p className="text-foreground">{selectedUser.full_name}</p>
              <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground/80 mb-1">Current Role</p>
              <span
                className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(selectedUser.role)}`}
              >
                <span>{getRoleIcon(selectedUser.role)}</span>
                <span>{selectedUser.role.replace('_', ' ')}</span>
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">New Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
              >
                {roles.map((role) => (
                  <option key={role.key} value={role.key}>
                    {getRoleIcon(role.key)} {role.name} ({role.permission_count} permissions)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end space-x-3">
            <button
              onClick={handleChangeRoleCloseClick}
              className="px-4 py-2 border border-input rounded-lg hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={() => handleRoleChange(selectedUser)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center space-x-2"
            >
              <Check size={16} />
              <span>Update Role</span>
            </button>
          </div>
        </Modal>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && (
        <Modal
          onClose={() => setShowPermissionsModal(null)}
          maxWidth="2xl"
          className="rounded-lg shadow-xl flex flex-col"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="text-lg font-medium flex items-center space-x-2">
              <span>{getRoleIcon(showPermissionsModal.key)}</span>
              <span>{showPermissionsModal.name} Permissions</span>
            </h3>
            <button
              onClick={() => setShowPermissionsModal(null)}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 gap-2">
              {showPermissionsModal.permissions.map((permission) => (
                <div key={permission} className="flex items-center space-x-2 p-2 bg-muted/50 rounded-lg">
                  <Check size={16} className="text-green-600 dark:text-green-400" />
                  <span className="text-sm text-foreground/80">{permission}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end">
            <button
              onClick={() => setShowPermissionsModal(null)}
              className="px-4 py-2 border border-input rounded-lg hover:bg-accent"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserRolesPage;
